import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import type { FundSetup, SetupValue } from '../../data/types';
import { blockGridColumns, type SetupSection } from '../../domain/setup/fieldTypes';
import { sectionErrors } from '../../domain/setup/setupRules';
import { useRegistry } from '../../store/RegistryStore';
import { SectionCard } from '../../ui/primitives';
import BrandScrapeButton from './BrandScrapeButton';
import SetupFieldInput from './SetupFieldInput';
import { BrandingPreview, CertificatePreview } from './SetupPreviews';

interface Props {
  setup: FundSetup;
  section: SetupSection;
  readOnly: boolean;
  onBack?: () => void;
  onNext: () => void;
}

export default function SetupSectionForm({ setup, section, readOnly, onBack, onNext }: Props) {
  const { state, dispatch } = useRegistry();
  const [attempted, setAttempted] = useState(false);
  const errors = sectionErrors(section.id, setup.values);
  const errorCount = Object.keys(errors).length;
  const preview =
    section.id === 'branding' ? (
      <BrandingPreview values={setup.values} />
    ) : section.id === 'class' ? (
      <CertificatePreview values={setup.values} />
    ) : null;

  const staffOptions = state.users
    .filter((user) => user.tenantId === setup.tenantId)
    .map((user) => ({ value: user.id, label: user.name }));

  const change = (key: string, value: SetupValue) =>
    dispatch({ type: 'SETUP_UPDATE', id: setup.id, patch: { [key]: value } });
  const chooseContact = (value: SetupValue) =>
    dispatch({ type: 'SETUP_CHOOSE_CONTACT', id: setup.id, userId: String(value) });

  const forms = (
    <Stack spacing={2}>
      {section.id === 'branding' && !readOnly && (
        <Box>
          <BrandScrapeButton />
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
            Pre-filled from the Fides defaults in Settings. Change any of them to brand this fund
            differently.
          </Typography>
        </Box>
      )}
      {section.blocks.map((block) => {
        const fields = block.fields.filter((field) => !field.showIf || field.showIf(setup.values));
        if (!fields.length) return null;
        return (
          <SectionCard key={block.title} title={block.title} subtitle={block.description}>
            <Box
              sx={{ display: 'grid', gap: 2, gridTemplateColumns: blockGridColumns(block.columns) }}
            >
              {fields.map((field) => {
                const edited = setup.values[field.key] !== setup.prefill[field.key];
                const staff = field.kind === 'staff';
                return (
                  <SetupFieldInput
                    key={field.key}
                    field={staff ? { ...field, kind: 'select', options: staffOptions } : field}
                    value={setup.values[field.key]}
                    prefill={setup.prefill[field.key]}
                    error={attempted || edited ? errors[field.key] : undefined}
                    readOnly={readOnly}
                    onChange={(value) => (staff ? chooseContact(value) : change(field.key, value))}
                  />
                );
              })}
            </Box>
          </SectionCard>
        );
      })}
    </Stack>
  );

  return (
    <>
      <Typography variant="h5" component="h2">
        {section.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {section.summary}
      </Typography>
      {readOnly && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {section.group === 'OFFER'
            ? 'The offer is live. Changes to a published offer are made from the Offers page.'
            : 'The register is active, so these details are now held on the fund record.'}
        </Alert>
      )}
      {preview ? (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 340px' },
          }}
        >
          {forms}
          <Box>
            <Typography variant="overline" color="text.secondary">
              Live preview
            </Typography>
            {preview}
          </Box>
        </Box>
      ) : (
        forms
      )}
      {attempted && errorCount > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {errorCount === 1 ? 'One field needs' : `${errorCount} fields need`} attention before you
          continue.
        </Alert>
      )}
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
        <Button disabled={!onBack} onClick={onBack}>
          Back
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            if (!readOnly && errorCount > 0) {
              setAttempted(true);
              return;
            }
            setAttempted(false);
            dispatch({ type: 'SETUP_COMPLETE_SECTION', id: setup.id, sectionId: section.id });
            onNext();
          }}
        >
          {readOnly ? 'Next' : 'Save and continue'}
        </Button>
      </Stack>
    </>
  );
}
